/**
 * AVG-blok voor /account: download alle data (JSON export) + verwijder account.
 * Verwijderen anonimiseert de user-doc na 7-jaar-fiscale-bewaarplicht check.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Download, Loader2, ShieldAlert, Trash2 } from "lucide-react";
import { api, API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function GdprBlock() {
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const { logout } = useAuth();
  const nav = useNavigate();

  const downloadExport = async () => {
    setExporting(true);
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API_BASE}/account/export.json`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download mislukt.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `mjp-account-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Je gegevens-export is gedownload.");
    } catch (e) {
      toast.error(e.message || "Download mislukt.");
    } finally {
      setExporting(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete", {
        password,
        confirm: confirmText,
      });
      toast.success("Account verwijderd. Je wordt uitgelogd.");
      setTimeout(async () => {
        await logout();
        nav("/login");
      }, 800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verwijderen mislukt.");
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="border-2 border-foreground bg-card heavy-shadow p-6 md:p-8" data-testid="gdpr-card">
        <div className="mb-4">
          <div className="flex items-center gap-2 stat-label">
            <ShieldAlert className="w-3.5 h-3.5" /> Privacy · AVG
          </div>
          <h2 className="font-display font-black text-2xl uppercase mt-1">Jouw data, jouw controle</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Volgens de AVG heb je recht op inzage in je gegevens en op verwijdering ervan.
            Zie ook onze <Link to="/privacy" className="underline hover:text-foreground" data-testid="gdpr-privacy-link">privacyverklaring</Link>.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="border-2 border-border p-4">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 shrink-0 mt-0.5 text-primary" />
              <div>
                <div className="font-semibold text-sm">Download al mijn gegevens</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Ontvang een JSON-bestand met álle data die we van je hebben: clubs, scenario's, historicals, budgetten, reserves, orders en account-instellingen. Wachtwoorden zijn (uiteraard) niet leesbaar.
                </p>
                <button
                  type="button"
                  onClick={downloadExport}
                  disabled={exporting}
                  data-testid="gdpr-export-btn"
                  className="mt-3 btn-brutal bg-background text-foreground border-2 border-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {exporting ? "Genereren…" : "Download JSON"}
                </button>
              </div>
            </div>
          </div>

          <div className="border-2 border-destructive/50 p-4">
            <div className="flex items-start gap-3">
              <Trash2 className="w-5 h-5 shrink-0 mt-0.5 text-destructive" />
              <div>
                <div className="font-semibold text-sm text-destructive">Verwijder mijn account</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Naam, e-mail, wachtwoord en factuurgegevens worden gewist; clubs en scenario's verwijderd. Facturen blijven 7 jaar bewaard (fiscale plicht) met een bevroren snapshot van je gegevens. Onomkeerbaar.
                </p>
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  data-testid="gdpr-delete-open"
                  className="mt-3 btn-brutal bg-destructive text-destructive-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Account verwijderen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {deleteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="gdpr-delete-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && !deleting) setDeleteOpen(false); }}
        >
          <div className="border-2 border-destructive bg-card heavy-shadow p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display font-black text-xl uppercase leading-tight text-destructive">
                  Account definitief verwijderen?
                </h2>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Dit is onomkeerbaar. Om te bevestigen: voer je huidige wachtwoord in, en typ letterlijk <code className="text-foreground font-mono-num">verwijder mijn account</code> in het bevestigingsveld.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="stat-label">Wachtwoord</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="gdpr-delete-password"
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-destructive text-sm"
                  autoComplete="current-password"
                />
              </div>
              <div>
                <label className="stat-label">Bevestiging</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  data-testid="gdpr-delete-confirm"
                  placeholder="verwijder mijn account"
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-destructive text-sm"
                />
              </div>
            </div>

            <div className="mt-5 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
                data-testid="gdpr-delete-cancel"
                className="btn-brutal bg-background text-foreground border-2 border-foreground px-4 py-2 font-display uppercase font-bold text-sm"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={deleting || !password || confirmText.trim().toLowerCase() !== "verwijder mijn account"}
                data-testid="gdpr-delete-submit"
                className="btn-brutal bg-destructive text-destructive-foreground px-4 py-2 font-display uppercase font-black text-sm inline-flex items-center gap-2 disabled:opacity-60"
              >
                {deleting ? <><Loader2 className="w-4 h-4 animate-spin" /> Bezig…</> : <>Definitief verwijderen</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
